import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, message, Typography, Tag, Tooltip, Popconfirm } from "antd";
import { Helmet } from "react-helmet";
import axios from "axios";
import dayjs from "dayjs";
import { CheckOutlined } from "@ant-design/icons";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;

const WebAppVersions = () => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [createPermission, setCreatePermission] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newVersion, setNewVersion] = useState("");

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const permissions = userData.permissions || {};
        setCreatePermission(permissions["signage.web_app_version_create"] === "1");
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/web-app-versions`,
                { params: { limit: pageSize, offset: (currentPage - 1) * pageSize } }
            );
            setDetails(response.data);
        } catch (error) {
            console.error("Error fetching web app versions", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    const handleAdd = async () => {
        if (!newVersion.trim()) return;
        try {
            setLoading(true);
            await axios.post(
                process.env.REACT_APP_BASE_URL + `/api/web-app-versions`,
                { version_number: newVersion.trim() }
            );
            message.success("Web app version added - browser players will reload onto it shortly.");
            setAddModalOpen(false);
            setNewVersion("");
            setCurrentPage(1);
            await fetchData();
        } catch (error) {
            console.error("Error adding web app version", error);
            message.error("Failed to add version");
        } finally {
            setLoading(false);
        }
    };

    const handleSetCurrent = async (id) => {
        try {
            setLoading(true);
            await axios.put(
                process.env.REACT_APP_BASE_URL + `/api/web-app-versions/${id}/set-current`
            );
            message.success("Version marked as current - browser players will reload onto it shortly.");
            await fetchData();
        } catch (error) {
            console.error("Error setting current web app version", error);
            message.error("Failed to set current version");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Version Number",
            dataIndex: "version_number",
            key: "version_number",
            render: (text, record) =>
                record.is_current ? (
                    <span>
                        {text} <Tag color="green">Current</Tag>
                    </span>
                ) : (
                    <span>{text}</span>
                ),
        },
        {
            title: "Added By",
            dataIndex: "created_by_name",
            key: "created_by_name",
            render: (text) => <span>{text ? text.trim() : ""}</span>,
        },
        {
            title: "Added At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => <span className="text-small">{dayjs(text).format("DD-MM-YYYY HH:mm")}</span>,
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) =>
                createPermission && (
                    <Tooltip title="Mark this version as current">
                        <Popconfirm
                            title="Mark this version as current? Browser players will reload onto it."
                            onConfirm={() => handleSetCurrent(record.id)}
                            okText="Yes"
                            cancelText="No"
                            disabled={!!record.is_current}
                        >
                            <Button
                                icon={<CheckOutlined />}
                                size="small"
                                disabled={!!record.is_current}
                            />
                        </Popconfirm>
                    </Tooltip>
                ),
        },
    ];

    return (
        <RequirePermission permission="signage.web_app_version_view">
            <div style={{ padding: "1rem" }}>
                <Helmet>
                    <title>{`${process.env.REACT_APP_NAME} | Web App`}</title>
                </Helmet>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Title level={3} style={{ marginBottom: "1rem" }}>
                        Web App
                    </Title>
                    <Button
                        type="primary"
                        onClick={() => setAddModalOpen(true)}
                        disabled={!createPermission}
                    >
                        Add Version
                    </Button>
                </div>
                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={details}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                    }}
                />
                <Modal
                    title="Add Web App Version"
                    open={addModalOpen}
                    onCancel={() => setAddModalOpen(false)}
                    onOk={handleAdd}
                    okText="Add"
                    okButtonProps={{ disabled: !newVersion.trim() }}
                >
                    <p>
                        Enter the version number for the web app build you just deployed.
                        Browser players will detect this and reload automatically.
                    </p>
                    <Input
                        value={newVersion}
                        onChange={(e) => setNewVersion(e.target.value)}
                        placeholder="e.g. 1.2.3"
                    />
                </Modal>
            </div>
        </RequirePermission>
    );
};

export default WebAppVersions;
